import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Tooltip,
  IconButton,
  Collapse,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  FileDownload,
  FilterAlt,
  FilterAltOff,
  Save,
  FolderOpen,
  Delete,
  ExpandMore,
  ExpandLess,
  Refresh,
  TableChart,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ── Field definitions ────────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  category: string;
  getValue: (emp: any) => string | number | null | undefined;
  filterable?: boolean;
  sortable?: boolean;
}

const FIELDS: FieldDef[] = [
  // Identity
  { key: 'employeeNumber', label: 'Employee #',    category: 'Identity',    getValue: (e) => e.employeeNumber,   filterable: false, sortable: true },
  { key: 'firstName',      label: 'First Name',    category: 'Identity',    getValue: (e) => e.firstName,        filterable: false, sortable: true },
  { key: 'lastName',       label: 'Last Name',     category: 'Identity',    getValue: (e) => e.lastName,         filterable: false, sortable: true },
  { key: 'fullName',       label: 'Full Name',     category: 'Identity',    getValue: (e) => [e.firstName, e.lastName].filter(Boolean).join(' '), filterable: false, sortable: true },
  { key: 'nickname',       label: 'Nickname',      category: 'Identity',    getValue: (e) => e.nickname,         filterable: false, sortable: true },
  // Contact
  { key: 'email',          label: 'Email',         category: 'Contact',     getValue: (e) => e.email,            filterable: false, sortable: true },
  { key: 'personalEmail',  label: 'Personal Email',category: 'Contact',     getValue: (e) => e.personalEmail,    filterable: false, sortable: false },
  { key: 'phoneNumber',    label: 'Phone',         category: 'Contact',     getValue: (e) => e.phoneNumber,      filterable: false, sortable: false },
  { key: 'country',        label: 'Country',       category: 'Contact',     getValue: (e) => e.country,          filterable: true,  sortable: true },
  { key: 'workLocation',   label: 'Work Location', category: 'Contact',     getValue: (e) => e.workLocation,     filterable: true,  sortable: true },
  { key: 'address',        label: 'Address',       category: 'Contact',     getValue: (e) => e.address,          filterable: false, sortable: false },
  // Employment
  { key: 'entity',         label: 'Entity',        category: 'Employment',  getValue: (e) => e.entity?.name,     filterable: true,  sortable: true },
  { key: 'department',     label: 'Department',    category: 'Employment',  getValue: (e) => e.department?.name, filterable: true,  sortable: true },
  { key: 'position',       label: 'Position',      category: 'Employment',  getValue: (e) => e.position?.title,  filterable: true,  sortable: true },
  { key: 'manager',        label: 'Manager',       category: 'Employment',  getValue: (e) => e.manager ? [e.manager.firstName, e.manager.lastName].filter(Boolean).join(' ') : '', filterable: true, sortable: true },
  { key: 'employmentStatus', label: 'Status',      category: 'Employment',  getValue: (e) => e.employmentStatus, filterable: true,  sortable: true },
  { key: 'employmentType', label: 'Emp. Type',     category: 'Employment',  getValue: (e) => e.employmentType,   filterable: true,  sortable: true },
  { key: 'startDate',      label: 'Start Date',    category: 'Employment',  getValue: (e) => e.startDate ? new Date(e.startDate).toLocaleDateString() : '', filterable: false, sortable: true },
  { key: 'endDate',        label: 'End Date',      category: 'Employment',  getValue: (e) => e.endDate ? new Date(e.endDate).toLocaleDateString() : '', filterable: false, sortable: true },
  { key: 'tenure',         label: 'Tenure (yrs)',  category: 'Employment',  getValue: (e) => {
    if (!e.startDate) return '';
    const months = (Date.now() - new Date(e.startDate).getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    return (months / 12).toFixed(1);
  }, filterable: false, sortable: true },
  // Personal
  { key: 'dateOfBirth',    label: 'Date of Birth', category: 'Personal',    getValue: (e) => e.dateOfBirth ? new Date(e.dateOfBirth).toLocaleDateString() : '', filterable: false, sortable: true },
  { key: 'gender',         label: 'Gender',        category: 'Personal',    getValue: (e) => e.gender,           filterable: true,  sortable: true },
  { key: 'nationality',    label: 'Nationality',   category: 'Personal',    getValue: (e) => e.nationality,      filterable: true,  sortable: true },
  // Emergency
  { key: 'emergencyContactName',  label: 'Emergency Contact', category: 'Emergency', getValue: (e) => e.emergencyContactName, filterable: false, sortable: false },
  { key: 'emergencyContactPhone', label: 'Emergency Phone',   category: 'Emergency', getValue: (e) => e.emergencyContactPhone, filterable: false, sortable: false },
];

const FIELD_CATEGORIES = ['Identity', 'Contact', 'Employment', 'Personal', 'Emergency'];
const DEFAULT_SELECTED = ['fullName', 'email', 'department', 'position', 'employmentStatus', 'startDate', 'tenure'];

// ── Filter types ─────────────────────────────────────────────────────────────

interface FilterDef {
  id: string;
  fieldKey: string;
  operator: 'equals' | 'contains' | 'notEquals' | 'isBlank' | 'isNotBlank';
  value: string;
}

// ── Saved template type ───────────────────────────────────────────────────────

interface ReportTemplate {
  name: string;
  selectedFields: string[];
  filters: FilterDef[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

const STORAGE_KEY = 'hris_custom_report_templates';

function loadTemplates(): ReportTemplate[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveTemplates(templates: ReportTemplate[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

// ── Main component ────────────────────────────────────────────────────────────

const CustomReport: React.FC = () => {
  const [employees, setEmployees]       = useState<any[]>([]);
  const [departments, setDepartments]   = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const [selectedFields, setSelectedFields] = useState<string[]>(DEFAULT_SELECTED);
  const [filters, setFilters]               = useState<FilterDef[]>([]);
  const [sortBy, setSortBy]                 = useState('fullName');
  const [sortDir, setSortDir]               = useState<'asc' | 'desc'>('asc');

  const [fieldsPanelOpen, setFieldsPanelOpen]   = useState(true);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  // Template dialogs
  const [saveDialogOpen, setSaveDialogOpen]     = useState(false);
  const [loadDialogOpen, setLoadDialogOpen]     = useState(false);
  const [templateName, setTemplateName]         = useState('');
  const [templates, setTemplates]               = useState<ReportTemplate[]>(loadTemplates);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false, msg: '', sev: 'success',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [emps, depts] = await Promise.all([
        apiService.getEmployees(),
        apiService.getDepartments(),
      ]);
      setEmployees(emps);
      setDepartments(depts);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived data ─────────────────────────────────────────────────────────

  const fieldMap = useMemo(() => Object.fromEntries(FIELDS.map((f) => [f.key, f])), []);

  const filteredEmployees = useMemo(() => {
    let result = [...employees];

    // Apply filters
    for (const f of filters) {
      const fieldDef = fieldMap[f.fieldKey];
      if (!fieldDef) continue;
      result = result.filter((emp) => {
        const val = String(fieldDef.getValue(emp) ?? '').toLowerCase();
        const filterVal = f.value.toLowerCase();
        switch (f.operator) {
          case 'equals':     return val === filterVal;
          case 'notEquals':  return val !== filterVal;
          case 'contains':   return val.includes(filterVal);
          case 'isBlank':    return !val;
          case 'isNotBlank': return !!val;
          default:           return true;
        }
      });
    }

    // Sort
    const sortFieldDef = fieldMap[sortBy];
    if (sortFieldDef) {
      result.sort((a, b) => {
        const aVal = String(sortFieldDef.getValue(a) ?? '').toLowerCase();
        const bVal = String(sortFieldDef.getValue(b) ?? '').toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }

    return result;
  }, [employees, filters, sortBy, sortDir, fieldMap]);

  const visibleFields = useMemo(
    () => FIELDS.filter((f) => selectedFields.includes(f.key)),
    [selectedFields],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const toggleField = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      { id: Date.now().toString(), fieldKey: 'department', operator: 'equals', value: '' },
    ]);
  };

  const updateFilter = (id: string, changes: Partial<FilterDef>) => {
    setFilters((prev) => prev.map((f) => f.id === id ? { ...f, ...changes } : f));
  };

  const removeFilter = (id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const exportCsv = () => {
    const headers = visibleFields.map((f) => f.label).join(',');
    const rows = filteredEmployees.map((emp) =>
      visibleFields.map((f) => {
        const val = String(f.getValue(emp) ?? '');
        return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
      }).join(','),
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ open: true, msg: `Exported ${filteredEmployees.length} rows`, sev: 'success' });
  };

  const saveTemplate = () => {
    if (!templateName.trim()) return;
    const tpl: ReportTemplate = { name: templateName.trim(), selectedFields, filters, sortBy, sortDir };
    const updated = [...templates.filter((t) => t.name !== tpl.name), tpl];
    setTemplates(updated);
    saveTemplates(updated);
    setSaveDialogOpen(false);
    setTemplateName('');
    setSnack({ open: true, msg: 'Template saved', sev: 'success' });
  };

  const loadTemplate = (tpl: ReportTemplate) => {
    setSelectedFields(tpl.selectedFields);
    setFilters(tpl.filters);
    setSortBy(tpl.sortBy);
    setSortDir(tpl.sortDir);
    setLoadDialogOpen(false);
    setSnack({ open: true, msg: `Loaded template "${tpl.name}"`, sev: 'success' });
  };

  const deleteTemplate = (name: string) => {
    const updated = templates.filter((t) => t.name !== name);
    setTemplates(updated);
    saveTemplates(updated);
  };

  const filterableFields = FIELDS.filter((f) => f.filterable);
  const filterOptions: Record<string, string[]> = useMemo(() => {
    const opts: Record<string, string[]> = {};
    for (const f of filterableFields) {
      const vals = Array.from(new Set(employees.map((e) => String(f.getValue(e) ?? '')).filter(Boolean))).sort();
      opts[f.key] = vals;
    }
    return opts;
  }, [employees, filterableFields]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <TableChart color="primary" />
          <Typography variant="h6" fontWeight={700}>Custom Report Builder</Typography>
          <Chip label={`${filteredEmployees.length} rows`} size="small" color="primary" variant="outlined" />
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} size="small"><Refresh /></IconButton>
          </Tooltip>
          <Button size="small" startIcon={<FolderOpen />} onClick={() => setLoadDialogOpen(true)} disabled={templates.length === 0}>
            Load ({templates.length})
          </Button>
          <Button size="small" startIcon={<Save />} onClick={() => setSaveDialogOpen(true)}>
            Save Template
          </Button>
          <Button size="small" variant="contained" startIcon={<FileDownload />} onClick={exportCsv} disabled={filteredEmployees.length === 0}>
            Export CSV
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Left panel — fields + filters */}
        <Grid size={{ xs: 12, md: 3 }}>

          {/* Field Selector */}
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box
              display="flex" alignItems="center" justifyContent="space-between"
              px={2} py={1} sx={{ cursor: 'pointer' }}
              onClick={() => setFieldsPanelOpen((v) => !v)}
            >
              <Typography variant="subtitle2" fontWeight={700}>Columns ({selectedFields.length})</Typography>
              {fieldsPanelOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={fieldsPanelOpen}>
              <Divider />
              <Box px={2} pb={2}>
                <Box display="flex" gap={1} mt={1} mb={1}>
                  <Button size="small" variant="text" onClick={() => setSelectedFields(FIELDS.map((f) => f.key))}>
                    All
                  </Button>
                  <Button size="small" variant="text" onClick={() => setSelectedFields(DEFAULT_SELECTED)}>
                    Reset
                  </Button>
                  <Button size="small" variant="text" color="error" onClick={() => setSelectedFields([])}>
                    None
                  </Button>
                </Box>
                {FIELD_CATEGORIES.map((cat) => {
                  const catFields = FIELDS.filter((f) => f.category === cat);
                  return (
                    <Box key={cat} mb={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {cat}
                      </Typography>
                      {catFields.map((f) => (
                        <FormControlLabel
                          key={f.key}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedFields.includes(f.key)}
                              onChange={() => toggleField(f.key)}
                              sx={{ py: 0.25 }}
                            />
                          }
                          label={<Typography variant="body2">{f.label}</Typography>}
                          sx={{ display: 'flex', ml: 0 }}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Paper>

          {/* Filter Panel */}
          <Paper variant="outlined">
            <Box
              display="flex" alignItems="center" justifyContent="space-between"
              px={2} py={1} sx={{ cursor: 'pointer' }}
              onClick={() => setFiltersPanelOpen((v) => !v)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <FilterAlt fontSize="small" color={filters.length > 0 ? 'primary' : 'disabled'} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Filters {filters.length > 0 ? `(${filters.length})` : ''}
                </Typography>
              </Box>
              {filtersPanelOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={filtersPanelOpen}>
              <Divider />
              <Box px={2} pb={2} pt={1}>
                {filters.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No filters applied. Click Add to filter by any field.</Typography>
                )}
                {filters.map((f, idx) => (
                  <Paper key={f.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" fontWeight={600}>Filter {idx + 1}</Typography>
                      <IconButton size="small" onClick={() => removeFilter(f.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Field</InputLabel>
                      <Select value={f.fieldKey} onChange={(e) => updateFilter(f.id, { fieldKey: e.target.value, value: '' })} label="Field">
                        {filterableFields.map((fd) => (
                          <MenuItem key={fd.key} value={fd.key}>{fd.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Condition</InputLabel>
                      <Select value={f.operator} onChange={(e) => updateFilter(f.id, { operator: e.target.value as any })} label="Condition">
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="notEquals">Not equals</MenuItem>
                        <MenuItem value="contains">Contains</MenuItem>
                        <MenuItem value="isBlank">Is blank</MenuItem>
                        <MenuItem value="isNotBlank">Is not blank</MenuItem>
                      </Select>
                    </FormControl>
                    {f.operator !== 'isBlank' && f.operator !== 'isNotBlank' && (
                      filterOptions[f.fieldKey]?.length > 0 ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Value</InputLabel>
                          <Select value={f.value} onChange={(e) => updateFilter(f.id, { value: e.target.value })} label="Value">
                            <MenuItem value="">Any</MenuItem>
                            {filterOptions[f.fieldKey].map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Value..."
                          value={f.value}
                          onChange={(e) => updateFilter(f.id, { value: e.target.value })}
                        />
                      )
                    )}
                  </Paper>
                ))}
                <Button size="small" startIcon={<FilterAlt />} onClick={addFilter} sx={{ mt: 0.5 }}>
                  Add Filter
                </Button>
                {filters.length > 0 && (
                  <Button size="small" color="error" startIcon={<FilterAltOff />} onClick={() => setFilters([])} sx={{ mt: 0.5, ml: 1 }}>
                    Clear All
                  </Button>
                )}
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Right panel — preview table */}
        <Grid size={{ xs: 12, md: 9 }}>
          {selectedFields.length === 0 ? (
            <Alert severity="info">Select at least one column to preview data.</Alert>
          ) : (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>#</TableCell>
                      {visibleFields.map((f) => (
                        <TableCell key={f.key} sx={{ fontWeight: 700, bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                          {f.sortable ? (
                            <TableSortLabel
                              active={sortBy === f.key}
                              direction={sortBy === f.key ? sortDir : 'asc'}
                              onClick={() => handleSort(f.key)}
                            >
                              {f.label}
                            </TableSortLabel>
                          ) : f.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEmployees.slice(0, 500).map((emp, idx) => (
                      <TableRow key={emp.id} hover>
                        <TableCell sx={{ color: 'text.disabled', fontSize: 11 }}>{idx + 1}</TableCell>
                        {visibleFields.map((f) => {
                          const val = f.getValue(emp);
                          const display = val ?? '—';
                          if (f.key === 'employmentStatus') {
                            const color = val === 'active' ? 'success' : val === 'terminated' ? 'error' : 'warning';
                            return (
                              <TableCell key={f.key}>
                                <Chip label={String(val || '—').toUpperCase()} color={color as any} size="small" />
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={f.key} sx={{ whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(display)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={visibleFields.length + 1} align="center">
                          <Typography color="text.secondary" py={3}>No employees match the current filters.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredEmployees.length > 500 && (
                      <TableRow>
                        <TableCell colSpan={visibleFields.length + 1} align="center">
                          <Typography variant="caption" color="text.secondary">
                            Showing first 500 rows — export CSV for full dataset ({filteredEmployees.length} rows)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save Report Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. Active Employees by Dept"
            sx={{ mt: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); }}
          />
          {templates.find((t) => t.name === templateName.trim()) && (
            <Alert severity="warning" sx={{ mt: 1 }}>A template with this name already exists and will be overwritten.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={!templateName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load Report Template</DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography color="text.secondary">No saved templates.</Typography>
          ) : (
            templates.map((t) => (
              <Paper key={t.name} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.selectedFields.length} columns · {t.filters.length} filter{t.filters.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={() => loadTemplate(t)}>Load</Button>
                  <IconButton size="small" color="error" onClick={() => deleteTemplate(t.name)}><Delete fontSize="small" /></IconButton>
                </Box>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomReport;
